import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SiInstagram } from "react-icons/si";
import { api, apiError } from "@/lib/api";
import PlatformIcon from "@/components/PlatformIcon";

const SOCIALS = [
  { name: "YouTube", href: "https://youtube.com/@destinytip", Icon: (p) => <PlatformIcon platform="youtube" {...p} /> },
  { name: "Instagram", href: "https://instagram.com/destinytip", Icon: (p) => <SiInstagram {...p} style={{ color: "#E879A8" }} /> },
  { name: "Kick", href: "https://kick.com/destinytip", Icon: (p) => <PlatformIcon platform="kick" {...p} /> },
  { name: "Twitch", href: "https://twitch.tv/destinytip", Icon: (p) => <PlatformIcon platform="twitch" {...p} /> },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const r = await api.post("/newsletter", { email });
      toast.success(r.data.message === "Subscribed" ? "You are on the list." : "You are already subscribed.");
      setEmail("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cols = [
    { title: "Platform", links: [["Features", "/features"], ["Pricing", "/pricing"], ["Live Creators", "/creators"], ["For Brands", "/brands"], ["Apply", "/apply"], ["Help Centre", "/help"]] },
    { title: "Company", links: [["Partners", "/partners"], ["Team", "/team"], ["Careers", "/careers"], ["Blog", "/blog"], ["Guides & Articles", "/help"], ["FAQ", "/faq"], ["Contact", "/contact"], ["Status", "/status"]] },
    { title: "Legal", links: [["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"], ["Cookie Policy", "/cookies"], ["Creator Application Policy", "/application-policy"], ["Data Deletion", "/data-deletion"]] },
  ];

  return (
    <footer className="bg-dt-bg2 border-t border-dt-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_2fr] gap-12">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/assets/logo.png" alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-dt-border" />
              <span className="font-heading font-bold tracking-[0.18em] text-sm">DESTINY TIP</span>
            </div>
            <p className="text-sm text-dt-muted leading-relaxed max-w-xs">
              Where every stream finds its destiny. Tools, tips, and community — in one place.
            </p>
            <div className="mt-5 flex items-center gap-2.5" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={`Destiny Tip on ${s.name}`}
                  className="w-9 h-9 rounded-full border border-dt-border bg-dt-card flex items-center justify-center text-dt-muted hover:border-dt-accent hover:scale-110 transition-all duration-200"
                  data-testid={`footer-social-${s.name.toLowerCase()}`}>
                  <s.Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="newsletter-form">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for product updates"
                className="flex-1 bg-dt-card border border-dt-border rounded-full px-4 py-2.5 text-sm text-dt-text placeholder:text-dt-muted focus:outline-none focus:border-dt-accent"
                data-testid="newsletter-email-input"
              />
              <button type="submit" disabled={busy}
                className="bg-dt-silver text-dt-bg font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-white transition-colors disabled:opacity-50"
                data-testid="newsletter-submit-btn">
                Join
              </button>
            </form>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            {cols.map((c) => (
              <div key={c.title}>
                <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-dt-silver mb-4">{c.title}</h4>
                <ul className="space-y-2.5">
                  {c.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith("/#") ? (
                        <a href={href} className="text-sm text-dt-muted hover:text-dt-text transition-colors">{label}</a>
                      ) : (
                        <Link to={href} className="text-sm text-dt-muted hover:text-dt-text transition-colors">{label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-dt-border flex flex-col sm:flex-row justify-between gap-4">
          <p className="text-xs text-dt-muted">© {new Date().getFullYear()} Destiny Tip. All rights reserved.</p>
          <p className="text-xs text-dt-muted max-w-xl" data-testid="footer-founder-line">
            Destiny Tip is founded &amp; built by <span className="text-dt-text font-semibold">Dipanshu</span> —
            with one aim: every creator receives 100% of every tip, straight to themselves.
          </p>
        </div>
      </div>
    </footer>
  );
}
